/**
 * Categories are free-form: the tool ships no fixed taxonomy (features.md §1).
 * Deleting one that is still in use is refused rather than silently orphaning
 * its entries.
 */

import type { Category, CategoryId, ChronoDocument } from '../../model/types';
import { isCategoryUsed } from '../../model/mutations';
import { field, h, iconButton } from './dom';

export interface CategoryPanelHandlers {
  onSubmit(name: string, color: string, id: CategoryId | null): void;
  onDelete(id: CategoryId): void;
  onSelect(id: CategoryId | null): void;
}

export function createCategoryPanel(handlers: CategoryPanelHandlers) {
  const name = h('input', { type: 'text', placeholder: 'ex: Scientifique' });
  const color = h('input', { type: 'color', value: '#cccccc' });

  const submit = h('button', { text: 'Ajouter', dataset: { action: 'category-submit' } });
  const cancel = h('button', { text: 'Annuler', hidden: true, dataset: { action: 'category-cancel' } });
  const error = h('div', { class: 'error', hidden: true });
  const summary = h('summary', { text: 'Catégories' });
  const list = h('ul', { class: 'item-list' });

  const el = h(
    'details',
    { class: 'panel', dataset: { panel: 'categories' } },
    summary,
    list,
    h('div', { class: 'form-row' }, field('Nom', name), field('Couleur', color), submit, cancel),
    error,
  );

  let editing: CategoryId | null = null;

  submit.addEventListener('click', () => {
    const text = name.value.trim();
    if (text === '') {
      error.hidden = false;
      error.textContent = 'Le nom est obligatoire.';
      return;
    }
    error.hidden = true;
    handlers.onSubmit(text, color.value, editing);
  });
  cancel.addEventListener('click', () => handlers.onSelect(null));

  return {
    el,

    setCategories(doc: ChronoDocument, selected: CategoryId | null): void {
      list.replaceChildren();

      for (const [id, category] of Object.entries(doc.categories)) {
        const used = isCategoryUsed(doc, id);
        const chip = h('i', {});
        chip.style.background = category.color;

        list.append(
          h(
            'li',
            { class: id === selected ? 'selected' : '' },
            chip,
            h('span', { text: category.name }),
            iconButton('edit', 'Modifier', () => handlers.onSelect(id)),
            iconButton(
              'delete',
              used ? 'Utilisée par au moins une entrée' : 'Supprimer',
              () => handlers.onDelete(id),
              { class: 'danger', disabled: used },
            ),
          ),
        );
      }

      if (Object.keys(doc.categories).length === 0) {
        list.append(h('li', { class: 'empty', text: 'Aucune catégorie — crées-en une avant d’ajouter des entrées.' }));
      }

      editing = selected;
      const current: Category | undefined = selected ? doc.categories[selected] : undefined;
      summary.textContent = current ? `Catégories — modification de « ${current.name} »` : 'Catégories';
      submit.textContent = current ? 'Enregistrer' : 'Ajouter';
      cancel.hidden = current === undefined;
      error.hidden = true;
      if (current) el.open = true;

      name.value = current?.name ?? '';
      color.value = current?.color ?? '#cccccc';
    },
  };
}
